import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Instagram, Facebook } from 'lucide-react';
import { contactInfo } from '../mock';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileDropdownOpen(null);
  }, [location.pathname]);

  const navigation = [
    { name: 'Home', path: '/' },
    { 
      name: 'Weddings', 
      path: '#',
      dropdown: [
        { name: 'Destination Weddings', path: '/destination-weddings' },
        { name: 'Wedding Décor', path: '/wedding-decor' },
        { name: 'Complete Celebration', path: '/complete-celebration' },
        { name: 'Sound & Entertainment', path: '/sound-entertainment' },
        { name: 'Events & Weddings', path: '/events-weddings' }
      ]
    },
    { name: 'Corporate Events', path: '/corporate-events' },
    { 
      name: 'Destinations', 
      path: '#',
      dropdown: [
        { name: 'Dharamshala', path: '/weddings/dharamshala' },
        { name: 'McLeodganj', path: '/weddings/mcleodganj' },
        { name: 'Palampur', path: '/weddings/palampur' },
        { name: 'Kangra', path: '/weddings/kangra' }
      ]
    },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
    { name: 'Blog', path: '/blog' }
  ];

  const isActive = (path) => location.pathname === path;
  const isDropdownActive = (dropdownName) => {
    if (dropdownName === 'Weddings') {
      return ['/destination-weddings', '/wedding-decor', '/complete-celebration', '/sound-entertainment', '/events-weddings'].includes(location.pathname);
    }
    if (dropdownName === 'Destinations') {
      return ['/weddings/dharamshala', '/weddings/mcleodganj', '/weddings/palampur', '/weddings/kangra'].includes(location.pathname);
    }
    return false;
  };

  // Desktop nav item styles
  const navItemStyle = {
    fontSize: '17px',
    fontWeight: 600,
    letterSpacing: '0.4px',
    lineHeight: '1.35',
    fontFamily: "'Lato', sans-serif"
  };

  // Mobile nav item styles
  const mobileNavItemStyle = {
    fontSize: '16px',
    fontWeight: 600,
    letterSpacing: '0.3px',
    lineHeight: '1.4',
    fontFamily: "'Lato', sans-serif"
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white shadow-lg' 
          : 'bg-gradient-to-b from-black/40 to-transparent'
      }`}
      data-testid="header"
    >
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <div className="flex h-20 sm:h-24 lg:h-28 items-center justify-between">
          
          {/* Premium Logo - Stylish Design */}
          <Link to="/" className="flex items-center group flex-shrink-0" data-testid="header-logo">
            {/* Original Logo Image */}
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_dreamy-weddings/artifacts/h0d1bmq6_ChatGPT%20Image%20Jan%2014%2C%202026%2C%2004_05_46%20PM.png"
                alt="Destination Weddings and Events Logo"
                className="h-12 sm:h-14 lg:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                style={{
                  filter: 'drop-shadow(0 2px 8px rgba(233, 30, 140, 0.3))'
                }}
              />
            </div>
            
            {/* Company Name Text - Stylish Premium Design */}
            <div className="hidden sm:flex flex-col ml-4 relative">
              {/* Decorative Line */}
              <div 
                className="absolute -left-2 top-1 bottom-1 w-[2px] rounded-full"
                style={{ 
                  background: 'linear-gradient(180deg, #E91E8C 0%, #FF1493 50%, #E91E8C 100%)'
                }}
              />
              
              <span 
                className={`text-lg lg:text-xl font-bold tracking-wider leading-tight transition-colors duration-300 ${
                  scrolled ? 'text-[#2C2C2C]' : 'text-white'
                }`}
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  textShadow: scrolled ? 'none' : '0 2px 15px rgba(0,0,0,0.5)',
                  letterSpacing: '2px'
                }}
              >
                DESTINATION
              </span>
              <div className="flex items-center gap-2">
                <span 
                  className="text-sm lg:text-base font-semibold tracking-widest"
                  style={{ 
                    background: 'linear-gradient(135deg, #E91E8C 0%, #FF1493 50%, #C71585 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontFamily: "'Lato', sans-serif",
                    letterSpacing: '3px'
                  }}
                >
                  WEDDINGS
                </span>
                <span 
                  className={`text-sm lg:text-base transition-colors duration-300 ${
                    scrolled ? 'text-[#E91E8C]' : 'text-[#FF69B4]'
                  }`}
                  style={{ 
                    fontFamily: "'Playfair Display', serif",
                    fontStyle: 'italic'
                  }}
                >
                  &
                </span>
                <span 
                  className="text-sm lg:text-base font-semibold tracking-widest"
                  style={{ 
                    background: 'linear-gradient(135deg, #E91E8C 0%, #FF1493 50%, #C71585 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontFamily: "'Lato', sans-serif",
                    letterSpacing: '3px'
                  }}
                >
                  EVENTS
                </span>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation - Centered with proper spacing */}
          <nav className="hidden lg:flex items-center" style={{ gap: '32px' }}>
            {navigation.map((item) => (
              item.dropdown ? (
                <div 
                  key={item.name} 
                  className="relative"
                  onMouseEnter={() => setActiveDropdown(item.name)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button
                    className={`flex items-center transition-all duration-300 ${
                      scrolled
                        ? isDropdownActive(item.name)
                          ? 'text-[#E91E8C]'
                          : 'text-[#1f1f1f] hover:text-[#E91E8C]'
                        : isDropdownActive(item.name)
                          ? 'text-[#FF1493]'
                          : 'text-white hover:text-[#FF1493]'
                    }`}
                    style={navItemStyle}
                    data-testid={`${item.name.toLowerCase().replace(/\s+/g, '-')}-dropdown`}
                  >
                    <span>{item.name}</span>
                    <ChevronDown 
                      className={`ml-2 h-4 w-4 transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  {/* Dropdown Menu */}
                  <div 
                    className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-200 ${
                      activeDropdown === item.name ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                    }`}
                  >
                    <div className="bg-white rounded-xl shadow-2xl py-3 min-w-[240px] border border-gray-100">
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          className={`block px-6 py-3.5 transition-all duration-200 ${
                            isActive(subItem.path)
                              ? 'text-[#E91E8C] bg-[#E91E8C]/5'
                              : 'text-[#1f1f1f] hover:text-[#E91E8C] hover:bg-[#E91E8C]/5'
                          }`}
                          style={{
                            fontSize: '15px',
                            fontWeight: 500,
                            fontFamily: "'Lato', sans-serif"
                          }}
                          data-testid={`dropdown-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`relative transition-all duration-300 ${
                    scrolled
                      ? isActive(item.path)
                        ? 'text-[#E91E8C]'
                        : 'text-[#1f1f1f] hover:text-[#E91E8C]'
                      : isActive(item.path)
                        ? 'text-[#FF1493]'
                        : 'text-white hover:text-[#FF1493]'
                  }`}
                  style={navItemStyle}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.name}
                  {/* Active indicator underline */}
                  {isActive(item.path) && (
                    <span 
                      className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full"
                      style={{ backgroundColor: scrolled ? '#E91E8C' : '#FF1493' }}
                    />
                  )}
                </Link>
              )
            ))}
          </nav>

          {/* Call Now Button & Social Icons - Desktop */}
          <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
            {/* Social Icons */}
            <div className="flex items-center gap-2">
              <a
                href={contactInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                  scrolled 
                    ? 'bg-[#E91E8C]/10 text-[#E91E8C] hover:bg-[#E91E8C] hover:text-white' 
                    : 'bg-white/20 text-white hover:bg-white hover:text-[#E91E8C]'
                }`}
                data-testid="header-instagram"
                aria-label="Visit our Instagram page"
              >
                <Instagram className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href={contactInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                  scrolled 
                    ? 'bg-[#E91E8C]/10 text-[#E91E8C] hover:bg-[#E91E8C] hover:text-white' 
                    : 'bg-white/20 text-white hover:bg-white hover:text-[#E91E8C]'
                }`}
                data-testid="header-facebook"
                aria-label="Visit our Facebook page"
              >
                <Facebook className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
            
            <button
              data-testid="header-cta-button"
              onClick={() => window.dispatchEvent(new CustomEvent('open-proposal'))}
              className="text-white transition-all duration-300 hover:scale-105 hover:shadow-xl flex items-center"
              style={{
                background: 'linear-gradient(135deg, #E91E8C 0%, #FF1493 100%)',
                fontFamily: "'Lato', sans-serif",
                fontSize: '14px',
                fontWeight: 600,
                padding: '14px 28px',
                borderRadius: '30px',
                letterSpacing: '0.3px',
                boxShadow: '0 6px 20px rgba(233, 30, 140, 0.35)'
              }}
            >
              💍 Get Free Proposal
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            className={`lg:hidden p-2.5 rounded-lg transition-all duration-200 ${
              scrolled 
                ? 'text-[#1f1f1f] hover:bg-gray-100' 
                : 'text-white hover:bg-white/10'
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {mobileMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>

        {/* Mobile Navigation - Accordion Style */}
        <div 
          className={`lg:hidden transition-all duration-300 ${
            mobileMenuOpen ? 'max-h-[calc(100dvh-150px)] overflow-y-auto overscroll-contain opacity-100' : 'max-h-0 overflow-hidden opacity-0'
          }`}
        >
          <div className="py-6 bg-white rounded-b-2xl shadow-xl">
            <div className="flex flex-col">
              {navigation.map((item) => (
                item.dropdown ? (
                  <div key={item.name} className="border-b border-gray-100 last:border-b-0">
                    {/* Accordion Header */}
                    <button
                      onClick={() => setMobileDropdownOpen(mobileDropdownOpen === item.name ? null : item.name)}
                      className={`w-full flex items-center justify-between px-6 py-4 transition-colors ${
                        isDropdownActive(item.name) ? 'text-[#E91E8C]' : 'text-[#1f1f1f]'
                      }`}
                      style={mobileNavItemStyle}
                      data-testid={`mobile-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span>{item.name}</span>
                      <ChevronDown 
                        className={`h-5 w-5 transition-transform duration-200 ${mobileDropdownOpen === item.name ? 'rotate-180' : ''}`}
                      />
                    </button>
                    
                    {/* Accordion Content */}
                    <div 
                      className={`overflow-hidden transition-all duration-200 bg-gray-50 ${
                        mobileDropdownOpen === item.name ? 'max-h-[300px]' : 'max-h-0'
                      }`}
                    >
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`block px-8 py-3.5 transition-colors ${
                            isActive(subItem.path)
                              ? 'text-[#E91E8C] bg-[#E91E8C]/10'
                              : 'text-[#1f1f1f] hover:text-[#E91E8C]'
                          }`}
                          style={{
                            fontSize: '15px',
                            fontWeight: 500,
                            fontFamily: "'Lato', sans-serif"
                          }}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`relative px-6 py-4 border-b border-gray-100 last:border-b-0 transition-colors ${
                      isActive(item.path)
                        ? 'text-[#E91E8C] bg-[#E91E8C]/5'
                        : 'text-[#1f1f1f] hover:text-[#E91E8C]'
                    }`}
                    style={mobileNavItemStyle}
                  >
                    <span className="flex items-center">
                      {item.name}
                      {isActive(item.path) && (
                        <span className="ml-2 w-1.5 h-1.5 rounded-full bg-[#E91E8C]" />
                      )}
                    </span>
                  </Link>
                )
              ))}
              
              {/* Mobile CTA Buttons */}
              <div className="px-6 pt-6 pb-2 space-y-3">
                <button
                  data-testid="mobile-cta-button"
                  onClick={() => { setMobileMenuOpen(false); window.dispatchEvent(new CustomEvent('open-proposal')); }}
                  className="w-full text-white py-4 flex items-center justify-center transition-all hover:opacity-90"
                  style={{
                    background: 'linear-gradient(135deg, #E91E8C 0%, #FF1493 100%)',
                    borderRadius: '30px',
                    fontSize: '15px',
                    fontWeight: 600,
                    fontFamily: "'Lato', sans-serif",
                    boxShadow: '0 4px 15px rgba(233, 30, 140, 0.3)'
                  }}
                >
                  💍 Get Your Free Wedding Proposal
                </button>
                <a href={`tel:${contactInfo.phones[0]}`} className="block">
                  <button 
                    className="w-full text-[#E91E8C] py-3 flex items-center justify-center transition-all border-2 border-[#E91E8C] rounded-full hover:bg-[#E91E8C]/5"
                    style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      fontFamily: "'Lato', sans-serif"
                    }}
                  >
                    <Phone className="mr-2 h-4 w-4" />
                    Talk to Wedding Expert
                  </button>
                </a>
                
                {/* Social Icons - Mobile */}
                <div className="flex items-center justify-center gap-4 pt-4">
                  <a
                    href={contactInfo.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#E91E8C]/10 flex items-center justify-center hover:bg-[#E91E8C] hover:text-white text-[#E91E8C] transition-all"
                    data-testid="mobile-instagram"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                  <a
                    href={contactInfo.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#E91E8C]/10 flex items-center justify-center hover:bg-[#E91E8C] hover:text-white text-[#E91E8C] transition-all"
                    data-testid="mobile-facebook"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
